import {
    DynamicVolumeSetKindEnum,
    IcaoPoint,
    IcaoPointType,
    LatLonPoint,
    RangeAndBearing,
    RefLocation,
    RefLocationRole,
    TerminalProcedureOrDct,
    TrafficVolumeSet,
    TvRefLocation,
} from 'app/model/CORE';
import Utils from './Utils';

/**
 * Utilty functions for Airspace
 * Inspired from GWT core.shared.dto.airspace and Client/CommonAirspaceUtils
 *
 */
export default class AirspaceUtils {
    /**
     * 39 ECTL member states, ICAO codes.
     *
     * Albania, Armenia, Austria, Belgium, Bosnia and Herzegovina, Bulgaria, Croatia, Cyprus, Czech Republic, Denmark,
     * Finland, France, Germany, Greece, Hungary, Ireland, Italy, Latvia, Lithuania, Luxembourg, Malta, Moldova, Monaco,
     * Montenegro, Netherlands, Norway, Poland, Portugal, Romania, Serbia, Slovakia, Slovenia, Spain, Sweden, Switzerland,
     * The former Yugoslav Republic of Macedonia, Turkey, Ukraine and United Kingdom of Great Britain and Northern Ireland.
     *
     * Note : Germain military ET is not included
     *      : Have included Monaco (LN) and Montenegro (LY).
     *
     * 01/03/2017 : added Georgia "UG" and Estonia "EE" to give 41 member states.
     */
    public static readonly ifpuzCountries = [
        'LA',
        'UG',
        'LO',
        'EB',
        'LQ',
        'LB',
        'LD',
        'LC',
        'LK',
        'EK',
        'EF',
        'LF',
        'ED',
        'LG',
        'LH',
        'EI',
        'LI',
        'EV',
        'EY',
        'EL',
        'LM',
        'LU',
        'LN',
        'LY',
        'EH',
        'EN',
        'EP',
        'LP',
        'LR',
        'LY',
        'LZ',
        'LJ',
        'LE',
        'ES',
        'LS',
        'LW',
        'LT',
        'UK',
        'EG',
        'UG',
        'EE',
    ];

    public static readonly ifpuzCooperatingCountries = ['GM', 'EE'];

    // = POINT UTILITIES

    public static readonly DBE_INDICATOR = '*';

    static getIcaoPointType(point: IcaoPoint): IcaoPointType {
        if (point.referencePoint) return IcaoPointType.REFERENCE_PT;
        if (point.pointId)
            return point.pointId.includes(AirspaceUtils.DBE_INDICATOR)
                ? IcaoPointType.DBE_PT
                : IcaoPointType.PUBLISHED_PT;
        return IcaoPointType.GEO_PT;
    }

    static toIcaoPointString(point: IcaoPoint): string {
        if (!point) return null;
        if (point.pointId) return point.pointId;
        if (point.position) return AirspaceUtils.toDDMMString(point.position);
        if (point.referencePoint) return AirspaceUtils.toRangeAndBearingString(point.referencePoint);
        return null;
    }

    static toDDMMString(point: LatLonPoint): string {
        const latAbs = Math.abs(point.latitude);
        const lonAbs = Math.abs(point.longitude);
        const iLatAbs = Math.floor(latAbs);
        const iLonAbs = Math.floor(lonAbs);
        return (
            iLatAbs +
            '\xB0' +
            Math.floor((latAbs - iLatAbs) * 60) +
            (point.longitude >= 0 ? 'N' : 'S') +
            ' / ' +
            iLonAbs +
            '\xB0' +
            Math.floor((lonAbs - iLonAbs) * 60) +
            (point.longitude >= 0 ? 'E' : 'W')
        );
    }

    static toRangeAndBearingString(rb: RangeAndBearing) {
        return rb ? rb.pointId + ':' + rb.rangeInNm + 'nm:' + rb.bearing + '\xB0' : null;
    }

    static toTerminalProcedureOrDctString(tp: TerminalProcedureOrDct) {
        if (!tp) return;
        return tp.direct ? 'DCT' : tp.routeId != null ? tp.routeId : tp.pointId;
    }

    // = functions taken from GWT CommonAirspaceUtils.

    /**
     * Return true if this airspaceId commences with a 2-leter code corresponding to an ECTL member state.
     * @param airspaceId
     */
    static isInIfpuz(airspaceId: string): boolean {
        if (!airspaceId || airspaceId.length < 2) return false;
        const countryId = Utils.toUpperCase(airspaceId.substring(0, 2));
        return AirspaceUtils.ifpuzCountries.includes(countryId);
    }

    // = functions below taken from GWT ClientAirspaceUtils.

    static toRefLocationString(refLoc: RefLocation) {
        return refLoc.refType + ' ' + refLoc.refId;
    }

    static toTvRefLocationString(refLoc: TvRefLocation) {
        const role = refLoc.role === RefLocationRole.G ? '' : '(' + refLoc.role + ')';
        return refLoc.refType + ' ' + refLoc.refId + role;
    }

    static toDynamicVolumeSetKindString(kind: DynamicVolumeSetKindEnum) {
        switch (kind) {
            case DynamicVolumeSetKindEnum.ARES_RESERVATION:
                return 'ARES';
            case DynamicVolumeSetKindEnum.ATC_VOLUME_DEFINITION:
                return 'ATC';
        }
        return '??'; // should never happen
    }

    static getTvSets(tvSets: Array<TrafficVolumeSet>, tvId: string) {
        const result = new Array<string>();
        tvSets.forEach(tvSet => {
            if (Utils.contains(tvSet.trafficVolumes, tvId)) {
                result.push(tvSet.name);
            }
        });
        return result;
    }
}
